/* ─────────────────────────────────────────────────────────────
   Endless Parks — Interactive Australia Parks Map
   Runtime for sections/endless-parks-map-v2.liquid.

   The section renders the shell + a JSON config block; this file
   builds the markers/list from window.EPM_DATA, then wires the
   interactions (filters, list, detail card) and the D3 map
   (projection, roads, troopy, critters, zoom). Per-section-id
   state (window['_epm_' + sid]) so multiple instances coexist
   (design system §12.6).

   Depends on: epm-d3.min.js, epm-data.js, epm-geo.js,
   epm-route-hwy1.js, epm-route-a87.js (all theme assets, loaded
   deferred by the section before this file).
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.__epmMapDefined) return;
  window.__epmMapDefined = true;

  var W = 900;
  var H = 760;

  var STATE_NAMES = {
    WA: 'Western Australia',
    TAS: 'Tasmania',
    NSW: 'New South Wales',
    VIC: 'Victoria',
    QLD: 'Queensland',
    NT: 'Northern Territory',
    SA: 'South Australia',
    ACT: 'Australian Capital Territory'
  };

  var FEATURED = /^(12 Apostles|Twelve Apostles|Uluru-Kata Tjuta|Great Barrier Reef|Kosciuszko|Cradle Mountain)/;

  /* classic muted multi-colour state fills (design system §3.5) */
  var CLASSIC_FILLS = {
    WA: '#C8A070', NT: '#C47848', QLD: '#E0C060', SA: '#A4BB8C',
    NSW: '#8AAAB8', VIC: '#5A8870', TAS: '#6880A0', ACT: '#9A8878'
  };

  /* Hand-drawn wildlife dropped onto the map by lon/lat. Land animals sit in
     the sparse inland gaps between park markers; sea life sits offshore. */
  var WILDLIFE = [
    { slot: 'kangaroo',   at: [140.5, -27.5], size: 30, motion: 'hop' },
    { slot: 'kangaroo',   at: [123.0, -25.5], size: 26, motion: 'hop', phase: 0.55 },
    { slot: 'emu',        at: [135.0, -26.0], size: 28, motion: 'stride' },
    { slot: 'kookaburra', at: [144.5, -22.5], size: 22, motion: 'perch' },
    { slot: 'koala',      at: [149.5, -30.5], size: 22, motion: 'cling' },
    { slot: 'wombat',     at: [145.5, -34.0], size: 22, motion: 'waddle' },
    { slot: 'echidna',    at: [120.0, -27.5], size: 20, motion: 'waddle' },
    { slot: 'whale',      at: [155.0, -27.0], size: 38, motion: 'cruise' },
    { slot: 'dolphin',    at: [114.0, -33.0], size: 30, motion: 'leap' },
    { slot: 'turtle',     at: [148.5, -18.0], size: 24, motion: 'paddle' },
    { slot: 'rays',       at: [112.5, -22.5], size: 28, motion: 'glide' },
    { slot: 'wave',       at: [128.0, -38.5], size: 20, motion: 'swell' },
    { slot: 'wave',       at: [138.5, -10.8], size: 18, motion: 'swell' },
    { slot: 'wave',       at: [153.5, -36.5], size: 20, motion: 'swell' },
    { slot: 'ripple',     at: [125.5, -13.0], size: 18, motion: 'ripple' },
    { slot: 'ripple',     at: [118.5, -36.0], size: 16, motion: 'ripple' }
  ];

  /* The longest state names (Australian Capital Territory, Northern Territory)
     wrap the detail card's badge onto a second line. Step the type down until
     the name fits, rather than shortening the name itself. Shared scope: both
     the interactions layer and the full screen resize handler call it. */
  function fitStateBadge(el) {
    if (!el || !el.parentNode) return;
    el.style.fontSize = '';
    el.style.letterSpacing = '';
    var avail = el.parentNode.clientWidth;
    if (!avail) return;
    var size = parseFloat(getComputedStyle(el).fontSize) || 11.5;
    var track = parseFloat(getComputedStyle(el).letterSpacing) / size || 0.1;
    var guard = 16;
    while (el.offsetWidth > avail && size > 7 && guard--) {
      size -= 0.4;
      track = Math.max(0.03, track - 0.007);
      el.style.fontSize = size + 'px';
      el.style.letterSpacing = track + 'em';
    }
  }

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ══════════════════════════════════════════════════════════
     Markup — markers overlay + park list, from EPM_DATA
     ══════════════════════════════════════════════════════════ */
  function buildMarkup(root, sid, cfg) {
    var data = window.EPM_DATA;
    var layer = root.querySelector('#' + sid + '-markers');
    var list = root.querySelector('#' + sid + '-list');
    if (!layer || !data) return;

    layer.textContent = '';
    data.parks.forEach(function (p) {
      var m = document.createElement('div');
      m.className = 'epm-marker' + (FEATURED.test(p.name) ? ' featured' : '');
      m.dataset.park = p.name;
      m.dataset.state = p.state;
      m.dataset.type = p.type;
      m.dataset.group = p.group;
      m.style.left = p.x + '%';
      m.style.top = p.y + '%';
      m.setAttribute('role', 'button');
      m.setAttribute('tabindex', '0');
      m.setAttribute('aria-label', p.name);

      var inner = document.createElement('div');
      inner.className = 'epm-marker-inner has-icon';
      var img = document.createElement('img');
      img.src = cfg.iconBase + 'epm-' + p.icon + '.png';
      img.alt = '';
      img.loading = 'lazy';
      img.draggable = false;
      /* missing icon asset → fall back to the navy dot */
      img.onerror = function () {
        inner.classList.remove('has-icon');
        inner.innerHTML = '<div class="epm-marker-dot"></div>';
      };
      inner.appendChild(img);
      m.appendChild(inner);

      var label = document.createElement('span');
      label.className = 'epm-label';
      label.textContent = p.name;
      m.appendChild(label);

      layer.appendChild(m);
    });

    if (list) {
      list.textContent = '';
      data.parks.slice().sort(function (a, b) {
        return a.state === b.state
          ? a.name.localeCompare(b.name)
          : a.state.localeCompare(b.state);
      }).forEach(function (p) {
        var li = document.createElement('li');
        li.dataset.park = p.name;
        li.dataset.state = p.state;
        li.dataset.group = p.group;
        if (cfg.listIcons) {
          var t = document.createElement('img');
          t.className = 'epm-list-thumb';
          t.src = cfg.iconBase + 'epm-' + p.icon + '.png';
          t.alt = '';
          t.loading = 'lazy';
          li.appendChild(t);
        } else {
          var dot = document.createElement('span');
          dot.className = 'epm-list-dot';
          li.appendChild(dot);
        }
        li.appendChild(document.createTextNode(p.name));
        var st = document.createElement('span');
        st.className = 'epm-list-state';
        st.textContent = p.state;
        li.appendChild(st);
        list.appendChild(li);
      });
    }
  }

  /* ══════════════════════════════════════════════════════════
     Interactions — filters, park list, detail card
     ══════════════════════════════════════════════════════════ */
  function initInteractions(root, sid, cfg) {
    var markers = Array.prototype.slice.call(root.querySelectorAll('.epm-marker'));
    var listItems = Array.prototype.slice.call(root.querySelectorAll('#' + sid + '-list li'));
    var detail = root.querySelector('#' + sid + '-detail');
    var countEl = root.querySelector('#' + sid + '-count');
    var headingEl = root.querySelector('#' + sid + '-list-heading');
    var filterBtns = Array.prototype.slice.call(root.querySelectorAll('.epm-filter-btn'));
    var productCards = Array.prototype.slice.call(root.querySelectorAll('.epm-product-card'));
    var bundleCards = Array.prototype.slice.call(root.querySelectorAll('.epm-bundle-card'));

    var activeState = cfg.defaultFilter || 'all';
    var activeMarker = null;

    function markerVisible(m, state) {
      if (state === 'all') return m.dataset.group === 'bottle';
      return m.dataset.group === 'mug' && m.dataset.state === state;
    }

    function filterLabel(state) {
      var btn = filterBtns.filter(function (b) { return b.dataset.state === state; })[0];
      return btn ? btn.textContent.trim() : state + ' Parks';
    }

    function updateProductCard(state) {
      productCards.forEach(function (card) {
        card.hidden = card.dataset.productState !== state;
      });
      bundleCards.forEach(function (card) {
        card.hidden = card.dataset.bundleState !== state;
      });
    }

    /* Bundle "add to cart" — adds every item in one request; the Campfire
       cart drawer picks the add up via its own fetch interceptor and opens. */
    bundleCards.forEach(function (card) {
      var btn = card.querySelector('[data-epm-bundle-add]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var ids = (btn.getAttribute('data-epm-bundle-add') || '').split(',').filter(Boolean);
        if (!ids.length) return;
        var items = ids.map(function (id) { return { id: parseInt(id, 10), quantity: 1 }; });
        var original = btn.textContent.trim();
        btn.disabled = true;
        btn.textContent = 'Adding\u2026';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (r) { return r.json(); }).then(function (json) {
          btn.disabled = false;
          if (json && json.status) {
            btn.textContent = 'Unavailable';
            setTimeout(function () { btn.textContent = original; }, 2000);
          } else {
            btn.textContent = original;
          }
        }).catch(function () {
          btn.disabled = false;
          btn.textContent = original;
        });
      });
    });

    function emptyDetail(state) {
      if (!detail) return;
      var sub = state === 'all'
        ? cfg.emptySubAll
        : (cfg.emptySubMug || '').replace('[state]', state);
      detail.classList.add('empty');
      detail.innerHTML =
                '<p class="epm-empty-t1"></p>' +
        '<p class="epm-empty-t2"></p>';
      detail.querySelector('.epm-empty-t1').textContent = cfg.emptyTitle || '';
      detail.querySelector('.epm-empty-t2').textContent = sub || '';
    }

    function showDetail(marker) {
      var name = marker.dataset.park;
      var state = marker.dataset.state;
      var group = marker.dataset.group;
      var img = marker.querySelector('img');
      var hasIcon = !!marker.querySelector('.epm-marker-inner.has-icon');

      if (detail) {
        var badge = group === 'bottle'
          ? (STATE_NAMES[state] || state)
          : (cfg.mugBadge || '[state] Adventure Mug').replace('[state]', state);

        detail.classList.remove('empty');
        detail.innerHTML =
          '<div class="epm-detail-row">' +
            '<div class="epm-detail-icon' + (hasIcon ? ' has-icon' : '') + '">' +
              (img && hasIcon
                ? '<img alt="">'
                : '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>') +
            '</div>' +
            '<div class="epm-detail-text">' +
              '<span class="epm-detail-state-badge"></span>' +
              '<div class="epm-detail-name"></div>' +
              '<div class="epm-detail-type"></div>' +
            '</div>' +
            (cfg.gmapsLink !== false
              ? '<a class="epm-detail-gmaps" target="_blank" rel="noopener" aria-label="View on Google Maps" title="View on Google Maps">' +
                  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 21s-7-6.1-7-11a7 7 0 0 1 14 0c0 4.9-7 11-7 11z"/><circle cx="12" cy="10" r="2.6"/></svg>' +
                '</a>'
              : '') +
          '</div>';
        if (img && hasIcon) {
          var di = detail.querySelector('.epm-detail-icon img');
          di.src = img.src;
          di.alt = name;
        }
        var badgeEl = detail.querySelector('.epm-detail-state-badge');
        badgeEl.textContent = badge;
        fitStateBadge(badgeEl);
        detail.querySelector('.epm-detail-name').textContent = name;
        detail.querySelector('.epm-detail-type').textContent = marker.dataset.type || '';
        var gmaps = detail.querySelector('.epm-detail-gmaps');
        if (gmaps) {
          gmaps.href = 'https://www.google.com/maps/search/?api=1&query=' +
            encodeURIComponent(name + ' ' + (marker.dataset.type || 'National Park') + ' Australia');
        }
      }

      markers.forEach(function (m) { m.classList.remove('active'); });
      marker.classList.add('active');

      listItems.forEach(function (li) { li.classList.remove('active'); });
      var matchLi = listItems.filter(function (li) {
        return li.dataset.park === name && li.dataset.group === group;
      })[0];
      if (matchLi) {
        matchLi.classList.add('active');
        var ul = matchLi.parentElement;
        ul.scrollTo({
          top: matchLi.offsetTop - ul.clientHeight / 2 + matchLi.offsetHeight / 2,
          behavior: 'smooth'
        });
      }
      activeMarker = marker;
    }

    function filterByState(state, animate) {
      activeState = state;
      var visibleNames = [];

      markers.forEach(function (m) {
        var match = markerVisible(m, state);
        m.classList.toggle('gone', !match);
        m.classList.remove('dimmed');
        if (match) visibleNames.push(m.dataset.park);
      });

      listItems.forEach(function (li) {
        var match = (state === 'all' && li.dataset.group === 'bottle') ||
                    (state !== 'all' && li.dataset.group === 'mug' && li.dataset.state === state);
        li.classList.toggle('hidden', !match);
      });

      if (countEl) countEl.textContent = visibleNames.length;
      if (headingEl) headingEl.textContent = filterLabel(state);
      updateProductCard(state);

      if (activeMarker && !markerVisible(activeMarker, state)) {
        emptyDetail(state);
        markers.forEach(function (m) { m.classList.remove('active'); });
        listItems.forEach(function (li) { li.classList.remove('active'); });
        activeMarker = null;
      } else if (!activeMarker) {
        /* keep the empty card's sub-line in sync with the filter */
        emptyDetail(state);
      }

      var api = window['_epm_' + sid];
      if (api && animate !== false) {
        if (state === 'all') api.zoomReset();
        else api.zoomToParkSet(visibleNames);
      }
    }

    markers.forEach(function (marker) {
      marker.addEventListener('click', function () {
        showDetail(marker);
        if (cfg.zoomOnSelect) {
          var api = window['_epm_' + sid];
          if (api) api.zoomToParkSet([marker.dataset.park]);
        }
      });
      marker.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          showDetail(marker);
        }
      });
    });

    listItems.forEach(function (li) {
      li.addEventListener('click', function () {
        var m = markers.filter(function (mk) {
          return mk.dataset.park === li.dataset.park && mk.dataset.group === li.dataset.group;
        })[0];
        if (m) showDetail(m);
        var api = window['_epm_' + sid];
        if (api) api.zoomToParkSet([li.dataset.park]);
      });
    });

    filterBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        filterBtns.forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        filterByState(btn.dataset.state);
      });
    });

    /* clicking the map background (markers live in an overlay above the
       SVG) deselects the current park; d3's clickDistance suppresses the
       click event after real drags, so panning doesn't deselect */
    var basemap = root.querySelector('#' + sid + '-svg');
    if (basemap) {
      basemap.addEventListener('click', function () {
        if (!activeMarker) return;
        markers.forEach(function (m) { m.classList.remove('active'); });
        listItems.forEach(function (li) { li.classList.remove('active'); });
        activeMarker = null;
        emptyDetail(activeState);
      });
    }

    /* initial state — no zoom animation on load */
    if (!filterBtns.some(function (b) { return b.dataset.state === activeState; })) {
      activeState = filterBtns.length ? filterBtns[0].dataset.state : 'all';
    }
    filterBtns.forEach(function (b) {
      b.classList.toggle('active', b.dataset.state === activeState);
    });
    filterByState(activeState, false);
    /* a state default (e.g. a mug's product page) starts zoomed into that
       state's parks — set instantly so there's no motion on load */
    if (activeState !== 'all') {
      var api0 = window['_epm_' + sid];
      var names0 = markers
        .filter(function (m) { return markerVisible(m, activeState); })
        .map(function (m) { return m.dataset.park; });
      if (api0 && names0.length) api0.zoomToParkSet(names0, true);
    }
  }

  /* ══════════════════════════════════════════════════════════
     D3 map — projection, states, roads, deco, zoom
     ══════════════════════════════════════════════════════════ */
  function initMap(root, sid, cfg) {
    var data = window.EPM_DATA;
    var svgEl = root.querySelector('#' + sid + '-svg');
    var wrap = root.querySelector('#' + sid + '-mapwrap');
    if (!svgEl || !wrap || !data) return;
    var svg = d3.select(svgEl);

    var g = svg.append('g').attr('class', 'epm-map-g');

    /* Fixed projection — hand-tuned; do not change (design system §12.2).
       fitSize/fitExtent fail silently in the Shopify context. */
    var projection = d3.geoMercator()
      .center([133.7, -27])
      .scale(900)
      .translate([450, 356]);

    var pathGen = d3.geoPath().projection(projection);

    var baseXY = {};
    Object.keys(data.coords).forEach(function (name) {
      var xy = projection(data.coords[name]);
      if (xy && isFinite(xy[0]) && isFinite(xy[1])) baseXY[name] = xy;
    });

    /* All theme-drawn SVG uses inline styles, never presentation
       attributes — theme CSS overrides attrs (design system §12.1). */
    var fills = cfg.stateFillStyle === 'classic' ? CLASSIC_FILLS : null;
    var borderOpacity = typeof cfg.borderOpacity === 'number' ? cfg.borderOpacity : 0.45;
    var paths = g.selectAll('path.epm-state-path')
      .data((window.EPM_GEO_HD || data.geo).features)
      .enter().append('path')
      .attr('class', 'epm-state-path')
      .attr('data-state', function (d) { return d.properties.state; })
      .attr('d', pathGen)
      .style('fill', function (d) {
        return fills ? (fills[d.properties.state] || '#ccc') : (cfg.landColor || '#CB6015');
      })
      .style('stroke', cfg.borderColor || '#ffffff')
      .style('stroke-opacity', borderOpacity)
      .style('stroke-width', '0.6px');

    /* state abbreviation labels */
    var labels = [];
    if (cfg.showStateLabels) {
      var labelFont = getComputedStyle(root).getPropertyValue('--epm-font-body') || 'sans-serif';
      var labelPos = {
        WA: [121.0, -26.0], NT: [133.0, -19.5], QLD: [144.5, -22.0],
        SA: [135.5, -30.0], NSW: [146.5, -33.0], VIC: [144.5, -37.2],
        TAS: [146.5, -42.0], ACT: [149.1, -35.5]
      };
      Object.keys(labelPos).forEach(function (st) {
        var xy = projection(labelPos[st]);
        var size = st === 'ACT' ? 7 : 12;
        var t = g.append('text')
          .attr('x', xy[0]).attr('y', xy[1])
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .style('fill', 'rgba(0,0,0,0.28)')
          .attr('font-size', size)
          .attr('font-family', labelFont)
          .attr('font-weight', '700')
          .attr('pointer-events', 'none')
          .attr('data-base-size', size)
          .text(st);
        labels.push(t);
      });
    }

    /* ── Decorative roads + troopy + critters ── */
    var deco = g.append('g').attr('class', 'epm-deco').attr('pointer-events', 'none');

    var decoImages = cfg.decoImages || {};
    var decoScale = cfg.decoScale || 1;

    function escUrl(u) { return String(u).replace(/"/g, '&quot;'); }

    /* hand-drawn car, front facing east (+x); colours parameterised so the
       second car reads as a different vehicle */
    function carArt(body, windows, swag) {
      return '' +
        '<path d="M2 11 L2 3.4 Q2 2.2 3.2 2.2 L17.6 2.2 Q18.6 2.2 19.2 3.2 L21 5.8 L24.6 6.2 Q26 6.5 26 8 L26 11 Z" fill="' + body + '" stroke="#3A2A1A" stroke-width="1.4" stroke-linejoin="round"/>' +
        '<path d="M18.4 3.4 L20.2 5.9 L17.6 5.9 L17.6 3.4 Z" fill="' + windows + '" stroke="#3A2A1A" stroke-width="0.9"/>' +
        '<rect x="4" y="3.6" width="4.6" height="2.6" rx="0.6" fill="' + windows + '" stroke="#3A2A1A" stroke-width="0.9"/>' +
        '<rect x="10" y="3.6" width="4.6" height="2.6" rx="0.6" fill="' + windows + '" stroke="#3A2A1A" stroke-width="0.9"/>' +
        '<path d="M2.4 9 L25.6 9" stroke="#C8A070" stroke-width="1.4"/>' +
        '<path d="M3.4 1.4 L16.8 1.4" stroke="#3A2A1A" stroke-width="1" stroke-linecap="round"/>' +
        '<path d="M5 2.2 L5 1.4 M15 2.2 L15 1.4" stroke="#3A2A1A" stroke-width="0.8"/>' +
        '<path d="M6.5 1.5 Q7 -0.6 9.5 -0.6 Q12 -0.6 12.5 1.5 Z" fill="' + swag + '" stroke="#3A2A1A" stroke-width="0.9"/>' +
        '<circle cx="25" cy="7.2" r="0.6" fill="#F2C94C"/>' +
        '<circle cx="6.5" cy="11" r="2.5" fill="#3A2A1A"/><circle cx="6.5" cy="11" r="0.9" fill="#FDF6EC"/>' +
        '<circle cx="21.5" cy="11" r="2.5" fill="#3A2A1A"/><circle cx="21.5" cy="11" r="0.9" fill="#FDF6EC"/>';
    }

    if (cfg.showRoads && window.EPM_ROUTE_HD) {
      /* both roads share one opacity group so the overlapping Darwin spur /
         common A87 section doesn't double-darken */
      var roads = deco.append('g').attr('opacity', 0.28);
      var hwySel = roads.append('path')
        .attr('id', sid + '-route')
        .attr('d', d3.line()(window.EPM_ROUTE_HD.map(projection)) + 'Z')
        .style('fill', 'none')
        .style('stroke', '#000')
        .style('stroke-width', 2.2)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round');
      if (window.EPM_ROUTE_A87) {
        roads.append('path')
          .attr('d', d3.line()(window.EPM_ROUTE_A87.map(projection)))
          .style('fill', 'none')
          .style('stroke', '#000')
          .style('stroke-width', 2.2)
          .attr('stroke-linecap', 'round')
          .attr('stroke-linejoin', 'round');
      }

      /* ── Cars: JS-driven so each can pace independently and flip to face
         its direction of travel (SMIL animateMotion can't do either) ── */
      var carsWanted = [];
      if (cfg.showTroopy) {
        carsWanted.push({
          img: decoImages.car1,
          lapMs: (cfg.troopyLapSeconds || 60) * 1000,
          offset: 0,
          body: '#FDF6EC', windows: '#BFD9E8', swag: '#CB6015'
        });
      }
      if (cfg.showTroopy2) {
        carsWanted.push({
          img: decoImages.car2,
          lapMs: (cfg.troopy2LapSeconds || 105) * 1000,
          offset: 0.45,
          body: '#BFD9E8', windows: '#FDF6EC', swag: '#1E3D70'
        });
      }

      if (carsWanted.length && !reducedMotion) {
        var hwyNode = hwySel.node();
        var hwyLen = hwyNode.getTotalLength();

        var cars = carsWanted.map(function (c) {
          var outer = deco.append('g');
          var inner;
          if (c.img) {
            var w = 30 * decoScale, h = 26 * decoScale;
            inner = '<g transform="translate(' + (-w / 2) + ',' + (-h / 2 - 2) + ')">' +
              '<image href="' + escUrl(c.img) + '" x="0" y="0" width="' + w + '" height="' + h + '" preserveAspectRatio="xMidYMid meet"/>' +
              '<animateTransform attributeName="transform" type="rotate" values="-2 ' + (w / 2) + ' ' + (h / 2) + ';2 ' + (w / 2) + ' ' + (h / 2) + ';-2 ' + (w / 2) + ' ' + (h / 2) + '" dur="0.6s" repeatCount="indefinite" additive="sum"/>' +
              '</g>';
          } else {
            inner = '<g transform="translate(-14,-12)">' + carArt(c.body, c.windows, c.swag) +
              '<animateTransform attributeName="transform" type="rotate" values="-2 14 7;2 14 7;-2 14 7" dur="0.6s" repeatCount="indefinite" additive="sum"/>' +
              '</g>';
          }
          outer.html(inner);
          return { g: outer, lapMs: c.lapMs, offset: c.offset, flip: 1 };
        });

        var driving = true;
        var rafId = null;
        function drive(now) {
          cars.forEach(function (c) {
            var s = ((now / c.lapMs + c.offset) % 1) * hwyLen;
            var p = hwyNode.getPointAtLength(s);
            /* face the direction of travel. The 16px lookahead + 3px
               hysteresis ignore the OSM trace's micro-zigzags (2px/0.15
               flipped the car ~100× per lap; these values give ~7 real
               reversals) and near-vertical stretches keep the last facing */
            var dx = hwyNode.getPointAtLength((s + 16) % hwyLen).x - p.x;
            if (Math.abs(dx) > 3) c.flip = dx < 0 ? -1 : 1;
            c.g.attr('transform', 'translate(' + p.x + ',' + p.y + ') scale(' + c.flip + ',1)');
          });
          rafId = requestAnimationFrame(drive);
        }
        rafId = requestAnimationFrame(drive);

        /* park the cars while the map is offscreen */
        if (window.IntersectionObserver) {
          new IntersectionObserver(function (entries) {
            var vis = entries[0].isIntersecting;
            if (vis && !driving) { driving = true; rafId = requestAnimationFrame(drive); }
            else if (!vis && driving) { driving = false; cancelAnimationFrame(rafId); }
          }).observe(wrap);
        }
      }
    }

    if (cfg.showCritters) {
      /* slot: which custom image replaces this critter's art; the bob
         animation is applied to the group either way */
      var addCritter = function (lonlat, art, dur, slot, boxW, boxH) {
        var xy = projection(lonlat);
        var bob = reducedMotion ? '' :
          '<animateTransform attributeName="transform" type="translate" values="0 0;0 -2.5;0 0" dur="' + dur + 's" repeatCount="indefinite" additive="sum"/>';
        var img = slot && decoImages[slot];
        if (img) {
          art = '<image href="' + escUrl(img) + '" x="0" y="0" width="' + (boxW * decoScale) + '" height="' + (boxH * decoScale) + '" preserveAspectRatio="xMidYMid meet"/>';
        }
        deco.append('g')
          .attr('transform', 'translate(' + xy[0] + ',' + xy[1] + ')')
          .html(art + bob);
      };
      var waveArt =
        '<path d="M0 3 Q2 0.5 4 3 Q6 5.5 8 3 Q10 0.5 12 3" fill="none" stroke="#6E93AE" stroke-width="1.4" stroke-linecap="round"/>' +
        '<path d="M3 6.5 Q5 4.5 7 6.5 Q9 8.5 11 6.5" fill="none" stroke="#6E93AE" stroke-width="1.1" stroke-linecap="round"/>';
      var whaleArt =
        '<path d="M1 9 Q3 3 10 3 Q16 3 17.5 8 L21 4.5 Q22.5 3.5 22 6 L20.5 9 L22 12 Q22.5 14.5 21 13.5 L17.5 10 Q15 13 9 12.5 Q3 12 1 9 Z" fill="#A9C6DA" stroke="#41617B" stroke-width="1.3" stroke-linejoin="round"/>' +
        '<circle cx="5.5" cy="7" r="0.8" fill="#41617B"/>' +
        '<path d="M2.5 9.5 Q5 10.5 7.5 9.8" fill="none" stroke="#41617B" stroke-width="0.9" stroke-linecap="round"/>' +
        '<path d="M7 2.5 Q6 0.5 4.8 0 M7 2.5 Q7 0.2 8.2 -0.5 M7 2.5 Q8.4 1 9.6 0.6" fill="none" stroke="#7FA8C9" stroke-width="1" stroke-linecap="round"/>';
      var dolphinArt =
        '<path d="M1 10 Q2 4 8 2.5 Q13 1.5 16 6 Q13 5.5 11.5 7 Q9 10.5 4.5 10.5 Q2.5 10.5 1 10 Z" fill="#BFD9E8" stroke="#41617B" stroke-width="1.2" stroke-linejoin="round"/>' +
        '<path d="M8 2.5 L7 -0.5 L10.2 1.4 Z" fill="#BFD9E8" stroke="#41617B" stroke-width="1.1" stroke-linejoin="round"/>' +
        '<circle cx="13.4" cy="4.6" r="0.7" fill="#41617B"/>';
      addCritter([110.5, -34], whaleArt, 4.2, 'whale', 24, 16);
      addCritter([155.5, -21], dolphinArt, 3.2, 'dolphin', 18, 12);
      addCritter([110, -22], waveArt, 3.6, 'wave', 13, 10);
      addCritter([127.5, -38.5], waveArt, 4.0, 'wave', 13, 10);
      addCritter([156, -31], waveArt, 3.0, 'wave', 13, 10);
      addCritter([138.5, -11.3], waveArt, 3.8, 'wave', 13, 10);
    }

    /* ── Wildlife: each drawing animated to match how the animal moves ── */
    if (cfg.showWildlife) {
      var wildArt = cfg.decoArt || {};

      var motionFor = function (kind, size, phase) {
        if (reducedMotion) return '';
        var u = size / 20;   /* keep the movement in proportion to the drawing */
        /* a negative begin starts the loop part-way through, so two of the
           same animal never move in lockstep */
        var off = phase ? ' begin="-' + phase + 's"' : '';
        var T = function (values, dur, extra) {
          return '<animateTransform attributeName="transform" type="translate" values="' +
            values + '" dur="' + dur + 's" repeatCount="indefinite" additive="sum"' + off +
            (extra || '') + '/>';
        };
        var R = function (values, dur) {
          return '<animateTransform attributeName="transform" type="rotate" values="' +
            values + '" dur="' + dur + 's" repeatCount="indefinite" additive="sum"' + off + '/>';
        };
        switch (kind) {
          /* a bound: springs up fast, hangs, drops back heavier */
          case 'hop':
            return T('0 0;0 ' + (-7 * u) + ';0 0', 1.15,
              ' calcMode="spline" keyTimes="0;0.42;1" keySplines="0.15 0.6 0.4 1;0.5 0 0.75 0.2"') +
              R('-5;4;-5', 1.15);
          /* long strides, body rocking over each leg */
          case 'stride':
            return T('0 0;0 ' + (-2.4 * u) + ';0 0', 0.9) + R('-2.5;2.5;-2.5', 1.8);
          /* low rolling gait */
          case 'waddle':
            return R('-3.5;3.5;-3.5', 2.6) + T('0 0;' + (1.6 * u) + ' 0;0 0', 5.2);
          /* dozing in the fork of a tree */
          case 'cling':
            return R('-2.2;2.2;-2.2', 6.5);
          /* sits still, then flicks its head */
          case 'perch':
            return T('0 0;0 ' + (-2.2 * u) + ';0 0;0 0', 3.6, ' keyTimes="0;0.05;0.11;1"') +
              R('0;-7;0;0', 3.6);
          /* breaks the surface and arcs back down */
          case 'leap':
            return T('0 0;0 ' + (-5 * u) + ';0 0', 2.8,
              ' calcMode="spline" keyTimes="0;0.5;1" keySplines="0.2 0.7 0.5 1;0.5 0 0.8 0.3"') +
              R('-9;9;-9', 2.8);
          /* slow, heavy roll through the swell */
          case 'cruise':
            return T('0 0;0 ' + (-2.6 * u) + ';0 0', 7) + R('-2;2;-2', 9);
          /* unhurried paddling */
          case 'paddle':
            return T('0 0;0 ' + (-2 * u) + ';0 0', 5) + R('-3;3;-3', 7.5);
          /* wings rippling as it glides */
          case 'glide':
            return T('0 0;0 ' + (-3 * u) + ';0 0', 4.4) + R('-4;4;-4', 8.8);
          /* swell rising and falling */
          case 'swell':
            return T('0 0;0 ' + (-2.2 * u) + ';0 0', 3.6);
          /* a ring spreading out and fading back */
          case 'ripple':
            return '<animateTransform attributeName="transform" type="scale" values="0.8;1.15;0.8" dur="4.5s" repeatCount="indefinite" additive="sum"/>' +
              '<animate attributeName="opacity" values="0.9;0.4;0.9" dur="4.5s" repeatCount="indefinite"/>';
        }
        return '';
      };

      WILDLIFE.forEach(function (w) {
        var url = wildArt[w.slot];
        if (!url) return;
        var xy = projection(w.at);
        var size = (w.size || 22) * decoScale;
        var half = size / 2;
        deco.append('g')
          .attr('transform', 'translate(' + xy[0] + ',' + xy[1] + ')')
          .attr('opacity', w.opacity == null ? 0.92 : w.opacity)
          .html('<image href="' + escUrl(url) + '" x="' + (-half) + '" y="' + (-half) +
            '" width="' + size + '" height="' + size + '" preserveAspectRatio="xMidYMid meet"/>' +
            motionFor(w.motion, size, w.phase));
      });
    }

    /* ── Marker overlay sync ── */
    var markerEls = Array.prototype.slice.call(root.querySelectorAll('#' + sid + '-markers .epm-marker'));

    var layerScale = 1;
    var lastTransform = d3.zoomIdentity;

    function measureLayer() {
      var r = wrap.getBoundingClientRect();
      if (r.width > 0 && r.width / W !== layerScale) {
        layerScale = r.width / W;
        /* re-place markers whenever the wrap resizes — the CSS can land
           after boot, and window resizes change the px-per-viewBox ratio */
        updateMarkers(lastTransform);
      }
    }

    function updateMarkers(transform) {
      lastTransform = transform;
      var k = transform.k, tx = transform.x, ty = transform.y;
      /* markers grow gently as you zoom in (capped so they never dominate) */
      var ms = Math.min(1.55, Math.pow(k, 0.3));
      /* cull against what is actually on screen: in full screen the wrap is
         the whole window, which covers far more than the viewBox's own box */
      var visW = layerScale > 0 ? wrap.clientWidth / layerScale : W;
      var visH = layerScale > 0 ? wrap.clientHeight / layerScale : H;
      markerEls.forEach(function (el) {
        var base = baseXY[el.dataset.park];
        if (!base) return;
        var x = base[0] * k + tx;
        var y = base[1] * k + ty;
        /* composited transform instead of left/top % — no layout thrash,
           stays smooth with 100+ markers during pan/zoom */
        el.style.left = '0';
        el.style.top = '0';
        el.style.transform = 'translate3d(' + (x * layerScale) + 'px,' + (y * layerScale) + 'px,0) translate(-50%,-50%) scale(' + ms + ')';
        el.style.visibility = (x > -30 && x < visW + 30 && y > -30 && y < visH + 30) ? 'visible' : 'hidden';
      });
    }

    measureLayer();
    updateMarkers(d3.zoomIdentity);
    if (window.ResizeObserver) new ResizeObserver(measureLayer).observe(wrap);

    var maxZoom = cfg.maxZoom || 14;
    var PAN_BOUNDS = [[-W * 0.3, -H * 0.3], [W * 1.3, H * 1.3]];
    var coarse = window.matchMedia('(pointer:coarse)').matches;
    var twoFinger = coarse && !cfg.freePan;

    var zoom = d3.zoom()
      .scaleExtent([1, maxZoom])
      /* d3 measures the viewport in element px but translateExtent below is in
         viewBox units; express the viewport in viewBox units so the two agree.
         Without this the map can lock in place when the wrap is the whole
         window (full screen) and panning stops working. */
      .extent(function () {
        var w = wrap.clientWidth, h = wrap.clientHeight;
        var s = w / W;
        return s > 0 ? [[0, 0], [w / s, h / s]] : [[0, 0], [W, H]];
      })
      /* padded extent so the map can be dragged even fully zoomed out */
      .translateExtent(PAN_BOUNDS)
      .clickDistance(6)
      .wheelDelta(function (event) {
        /* gentler, smoother wheel steps than the d3 default */
        var delta = -event.deltaY * (event.deltaMode === 1 ? 0.06 : event.deltaMode ? 1 : 0.0016);
        /* pinch deltas are far smaller than a wheel notch's, so scale them up */
        return event.ctrlKey ? delta * 10 : delta;
      })
      .filter(function (event) {
        /* on touch devices require two fingers so one finger scrolls the page;
           full screen has no page behind it, so one finger pans there */
        if (twoFinger && !root.classList.contains('epm--fs') && event.type === 'touchstart') {
          return event.touches.length >= 2;
        }
        /* A trackpad pinch reaches the page as ctrl+wheel, which the usual
           filter rejects so the browser can zoom the page instead. In full
           screen there is no page to zoom, so take it and pinch the map. */
        if (event.type === 'wheel' && event.ctrlKey) {
          return root.classList.contains('epm--fs');
        }
        return !event.ctrlKey && !event.button;
      })
      .on('start', function () { wrap.classList.add('is-dragging'); })
      .on('end', function () { wrap.classList.remove('is-dragging'); })
      .on('zoom', function (event) {
        var t = event.transform;
        g.attr('transform', t);
        paths.style('stroke-width', (0.6 / t.k) + 'px');
        labels.forEach(function (label) {
          var base = +label.attr('data-base-size');
          label.attr('font-size', Math.max(6, base / t.k));
        });
        updateMarkers(t);
      });

    svg.call(zoom);

    /* One-finger mode: a drag that STARTS on a marker icon still pans the
       map — the zoom behavior lives on the svg and never sees touches that
       begin on the HTML marker layer, so forward them manually. A clean tap
       (under 8px of movement) still opens the park. */
    if (coarse && cfg.freePan) {
      var mDrag = null;
      var mMoved = false;
      var mSuppressTap = false;
      wrap.addEventListener('touchstart', function (e) {
        var mk = e.target.closest && e.target.closest('.epm-marker');
        if (!mk || e.touches.length !== 1) { mDrag = null; return; }
        var t = e.touches[0];
        mDrag = { x: t.clientX, y: t.clientY };
        mMoved = false;
      }, { passive: true });
      wrap.addEventListener('touchmove', function (e) {
        if (!mDrag || e.touches.length !== 1) return;
        var t = e.touches[0];
        var dx = t.clientX - mDrag.x;
        var dy = t.clientY - mDrag.y;
        if (!mMoved && Math.hypot(dx, dy) < 8) return;
        mMoved = true;
        e.preventDefault();
        var r = svgEl.getBoundingClientRect();
        var f = W / r.width; /* css px -> viewBox units */
        var k = d3.zoomTransform(svgEl).k;
        zoom.translateBy(svg, dx * f / k, dy * f / k);
        mDrag.x = t.clientX;
        mDrag.y = t.clientY;
      }, { passive: false });
      wrap.addEventListener('touchend', function () {
        if (mDrag && mMoved) {
          mSuppressTap = true;
          setTimeout(function () { mSuppressTap = false; }, 400);
        }
        mDrag = null;
      });
      wrap.addEventListener('click', function (e) {
        if (mSuppressTap && e.target.closest && e.target.closest('.epm-marker')) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, true);
    }

    /* double-click / double-tap: 2× about the clicked point. Build a fresh
       transform from the inverted pointer — chained scale().translate() drifts. */
    svg.on('dblclick.zoom', function (event) {
      event.preventDefault();
      var t = d3.zoomTransform(svgEl);
      var pt = d3.pointer(event, svgEl);
      var p = t.invert(pt);
      var k2 = Math.min(t.k * 2, maxZoom);
      svg.transition().duration(300).ease(d3.easeCubicOut).call(
        zoom.transform,
        d3.zoomIdentity.translate(W / 2 - k2 * p[0], H / 2 - k2 * p[1]).scale(k2)
      );
    });

    /* d3 applies the zoom filter before counting taps, so the two-finger
       filter above also disables its built-in double-tap. Re-add it here. */
    if (twoFinger) {
      var lastTapAt = 0;
      var lastTapXY = null;
      wrap.addEventListener('touchend', function (e) {
        if (root.classList.contains('epm--fs')) return;
        if (e.changedTouches.length !== 1 || e.touches.length) return;
        if (e.target.closest('.epm-marker, .epm-zoom-controls')) return;
        var t = e.changedTouches[0];
        var now = e.timeStamp;
        var xy = [t.clientX, t.clientY];
        if (lastTapXY && now - lastTapAt < 300 &&
            Math.abs(xy[0] - lastTapXY[0]) < 30 && Math.abs(xy[1] - lastTapXY[1]) < 30) {
          lastTapAt = 0;
          lastTapXY = null;
          var cur = d3.zoomTransform(svgEl);
          var r = svgEl.getBoundingClientRect();
          var p = cur.invert([
            (xy[0] - r.left) / r.width * W,
            (xy[1] - r.top) / r.height * H
          ]);
          var k2 = Math.min(cur.k * 2, maxZoom);
          svg.transition().duration(300).ease(d3.easeCubicOut).call(
            zoom.transform,
            d3.zoomIdentity.translate(W / 2 - k2 * p[0], H / 2 - k2 * p[1]).scale(k2)
          );
        } else {
          lastTapAt = now;
          lastTapXY = xy;
        }
      }, { passive: true });
    }

    /* ── Zoom buttons ── */
    if (cfg.showZoomButtons) {
      var controls = document.createElement('div');
      controls.className = 'epm-zoom-controls';
      controls.innerHTML =
        '<button type="button" class="epm-zoom-btn" data-action="in" aria-label="Zoom in">+</button>' +
        '<button type="button" class="epm-zoom-btn" data-action="out" aria-label="Zoom out">−</button>' +
        '<button type="button" class="epm-zoom-btn" data-action="reset" aria-label="Reset view" title="Reset">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M3 12a9 9 0 1 0 9-9 9 9 0 0 0-6.17 2.38"/><path d="M3 3v5h5"/></svg>' +
        '</button>';
      wrap.appendChild(controls);

      controls.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-action]');
        if (!btn) return;
        var action = btn.dataset.action;
        if (action === 'in') svg.transition().duration(280).ease(d3.easeCubicOut).call(zoom.scaleBy, 1.7);
        if (action === 'out') svg.transition().duration(280).ease(d3.easeCubicOut).call(zoom.scaleBy, 1 / 1.7);
        if (action === 'reset') {
          /* identity is the in-page view; full screen has its own fitted view */
          if (fsOn()) fsRefit(false);
          else svg.transition().duration(500).ease(d3.easeCubicInOut).call(zoom.transform, d3.zoomIdentity);
        }
      });
    }

    /* ── Full screen ── */
    var fsSidebar = root.querySelector('.epm-sidebar');

    function fsOn() { return root.classList.contains('epm--fs'); }

    /* In full screen the wrap IS the window and the SVG paints past its own
       box, so the map keeps covering the screen while you pan. Fitting is
       therefore done in screen pixels: px = (v * k + t) * s, where s is the
       px-per-viewBox-unit the markers also use. */
    function fsFitNames(names, instant) {
      var vw = wrap.clientWidth;
      var vh = wrap.clientHeight;
      if (!vw || !vh) return;
      var pts = (names || []).map(function (n) { return baseXY[n]; }).filter(Boolean);
      if (!pts.length) return;
      var xs = pts.map(function (p) { return p[0]; });
      var ys = pts.map(function (p) { return p[1]; });
      var s = vw / W;
      var padPx = 56;   /* room for the marker badges around the edge parks */
      var topPx = 66;   /* clear the filter row */
      var botPx = (fsSidebar ? Math.min(fsSidebar.offsetHeight, vh * 0.5) : 0) + 14;
      var availH = Math.max(120, vh - topPx - botPx);
      var padW = padPx / s;
      var x0 = Math.min.apply(null, xs) - padW;
      var x1 = Math.max.apply(null, xs) + padW;
      var y0 = Math.min.apply(null, ys) - padW;
      var y1 = Math.max.apply(null, ys) + padW;
      var k = Math.min(vw / ((x1 - x0) * s), availH / ((y1 - y0) * s));
      k = Math.max(0.25, Math.min(k, maxZoom));
      fsSetPanBounds();
      /* the fitted view sits below the normal minimum zoom, so let people
         zoom back out to it while full screen */
      zoom.scaleExtent([Math.min(1, k * 0.92), maxZoom]);
      var cx = (x0 + x1) / 2;
      var cy = (y0 + y1) / 2;
      var t = d3.zoomIdentity
        .translate((vw / 2) / s - cx * k, (topPx + availH / 2) / s - cy * k)
        .scale(k);
      /* transitions are driven by animation frames, which are paused while the
         page is hidden — apply straight away then, so the view still lands */
      if (instant || document.hidden) svg.call(zoom.transform, t);
      else svg.transition().duration(450).ease(d3.easeCubicInOut).call(zoom.transform, t);
    }

    /* d3 re-centres an axis whenever the pan bounds are smaller than the
       viewport, which locks the map horizontally once it fills the window.
       Full screen therefore gets bounds measured from the viewport itself. */
    function fsSetPanBounds() {
      var vw = wrap.clientWidth, vh = wrap.clientHeight;
      if (!vw || !vh) return;
      var vhWorld = vh / (vw / W);
      zoom.translateExtent([[-W, -vhWorld], [W * 2, H + vhWorld]]);
    }

    function fsRefit(instant) {
      if (!fsOn()) return;
      fsFitNames(markerEls
        .filter(function (el) { return !el.classList.contains('gone'); })
        .map(function (el) { return el.dataset.park; }), instant);
    }

    var fsBtn = root.querySelector('[data-epm-fs]');
    if (fsBtn) {
      var fsScrollY = 0;
      var fsPrevTransform = null;

      var setFs = function (on) {
        if (on) {
          fsScrollY = window.pageYOffset;
          fsPrevTransform = d3.zoomTransform(svgEl);
        }
        root.classList.toggle('epm--fs', on);
        document.documentElement.classList.toggle('epm-fs-open', on);
        fsBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        wrap.style.width = '';
        wrap.style.height = '';
        wrap.style.left = '';
        wrap.style.top = '';
        if (on) {
          /* fit only once the overlay has actually been laid out, otherwise the
             bottom cards still measure 0 and the map is fitted too large.
             Timers, not rAF: rAF is paused while the tab is in the background. */
          setTimeout(function () { fsRefit(true); }, 0);
          /* card images can land late and change the reserved height */
          setTimeout(function () { fsRefit(true); }, 320);
        } else {
          zoom.scaleExtent([1, maxZoom]);
          zoom.translateExtent(PAN_BOUNDS);
          if (fsPrevTransform) {
            /* the markers re-measure themselves when the wrap resizes back */
            svg.call(zoom.transform, fsPrevTransform);
          }
          /* the section leaves the flow while fixed, which can shorten the page */
          window.scrollTo(0, fsScrollY);
        }
      };

      fsBtn.addEventListener('click', function () { setFs(!fsOn()); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && fsOn()) setFs(false);
      });
      window.addEventListener('resize', function () { fsRefit(true); });
      window.addEventListener('resize', function () {
        fitStateBadge(root.querySelector('.epm-detail-state-badge'));
      });
    }

    /* ── public API for the interactions layer ── */
    window['_epm_' + sid] = {
      zoomReset: function () {
        if (fsOn()) { fsRefit(false); return; }
        svg.transition().duration(500).ease(d3.easeCubicInOut).call(zoom.transform, d3.zoomIdentity);
      },
      zoomToParkSet: function (names, instant) {
        if (fsOn()) { fsFitNames(names, instant); return; }
        var points = names
          .map(function (n) { return baseXY[n]; })
          .filter(Boolean);
        if (!points.length) return;
        var xs = points.map(function (p) { return p[0]; });
        var ys = points.map(function (p) { return p[1]; });
        var x0 = Math.min.apply(null, xs) - 50;
        var y0 = Math.min.apply(null, ys) - 50;
        var x1 = Math.max.apply(null, xs) + 50;
        var y1 = Math.max.apply(null, ys) + 50;
        var scale = Math.min(maxZoom * 0.86, 0.88 * Math.min(W / (x1 - x0), H / (y1 - y0)));
        var tx = W / 2 - scale * (x0 + x1) / 2;
        var ty = H / 2 - scale * (y0 + y1) / 2;
        var t = d3.zoomIdentity.translate(tx, ty).scale(scale);
        if (instant) {
          svg.call(zoom.transform, t);
          return;
        }
        svg.transition().duration(550).ease(d3.easeCubicInOut).call(zoom.transform, t);
      }
    };

    /* ── one-finger drag hint on touch devices ── */
    if (twoFinger) {
      var touchHint = document.createElement('div');
      touchHint.className = 'epm-touch-hint';
      touchHint.textContent = cfg.touchHintText || 'Use two fingers to move the map';
      wrap.appendChild(touchHint);
      var touchHintTimer = null;
      var touchHintShown = 0;
      var panStart = null;
      wrap.addEventListener('touchstart', function (e) {
        panStart = e.touches.length === 1
          ? [e.touches[0].clientX, e.touches[0].clientY]
          : null;
      }, { passive: true });
      wrap.addEventListener('touchmove', function (e) {
        if (root.classList.contains('epm--fs')) return;
        if (e.touches.length > 1) {
          panStart = null;
          touchHint.classList.remove('show');
          return;
        }
        if (!panStart || touchHintShown >= 2) return;
        var dx = Math.abs(e.touches[0].clientX - panStart[0]);
        var dy = Math.abs(e.touches[0].clientY - panStart[1]);
        /* vertical drags are the page scrolling past — only a sideways drag
           means they were actually trying to move the map */
        if (dy > dx || dx < 24) return;
        touchHint.classList.add('show');
        clearTimeout(touchHintTimer);
        touchHintTimer = setTimeout(function () {
          touchHint.classList.remove('show');
          touchHintShown++;
        }, 1200);
      }, { passive: true });
      wrap.addEventListener('touchend', function () { panStart = null; }, { passive: true });
    }

    /* ── fading hint bar under the map (skip on touch in one-finger mode:
       dragging is natural, there is nothing to explain) ── */
    if (cfg.showHint && !(coarse && cfg.freePan)) {
      var hint = document.createElement('div');
      hint.className = 'epm-zoom-hint';
      hint.textContent = coarse
        ? (cfg.hintTextTouch || 'Use two fingers to move the map · double-tap to zoom')
        : (cfg.hintTextMouse || 'Scroll to zoom · drag to pan · double-click to zoom in');
      wrap.appendChild(hint);

      var hintTimer = setTimeout(function () {
        hint.classList.add('fade');
        setTimeout(function () { hint.remove(); }, 1000);
      }, 3500);

      svg.on('zoom.hint', function () {
        clearTimeout(hintTimer);
        hint.classList.add('fade');
        setTimeout(function () { hint.remove(); }, 600);
        svg.on('zoom.hint', null);
      });
    }

    wrap.classList.add('is-ready');
  }

  /* ══════════════════════════════════════════════════════════
     Boot — one instance per section, deps may still be loading
     ══════════════════════════════════════════════════════════ */
  function boot(el) {
    if (el.__epmInit) return;
    var sid = el.dataset.sectionId;
    if (!sid) return;

    /* in the theme editor the asset scripts can land after this file —
       poll briefly until d3 + data are present */
    if (!(window.d3 && window.EPM_DATA && window.EPM_GEO_HD)) {
      el.__epmRetries = (el.__epmRetries || 0) + 1;
      if (el.__epmRetries < 80) setTimeout(function () { boot(el); }, 150);
      return;
    }
    el.__epmInit = true;

    var cfg = {};
    var cfgEl = document.getElementById(sid + '-config');
    if (cfgEl) {
      try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { cfg = {}; }
    }

    buildMarkup(el, sid, cfg);
    initMap(el, sid, cfg);
    initInteractions(el, sid, cfg);
  }

  function bootAll() {
    document.querySelectorAll('epm-map[data-section-id]').forEach(boot);
  }

  if (window.customElements && !customElements.get('epm-map')) {
    customElements.define('epm-map', class extends HTMLElement {
      connectedCallback() { boot(this); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  /* theme editor: re-init when the merchant edits section settings */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('epm-map[data-section-id]');
    if (el) boot(el);
  });
})();

/* ── Bundle option modal: pick colours before the bundle add ────────────
   Openers carry data-epm-modal-open="<modal id>"; the modal's confirm
   button keeps data-epm-bundle-add (bound by the handler above before the
   modal is teleported to <body>, so the add itself is unchanged). */
(function () {
  'use strict';

  function closeModal(m) {
    m.hidden = true;
    document.documentElement.classList.remove('epm-modal-open');
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-epm-modal-open]');
    if (opener) {
      var m = document.getElementById(opener.getAttribute('data-epm-modal-open'));
      if (m) {
        m.hidden = false;
        document.documentElement.classList.add('epm-modal-open');
      }
      return;
    }
    var closer = e.target.closest('[data-epm-modal-close]');
    if (closer) {
      var cm = closer.closest('[data-epm-bundle-modal]');
      if (cm) closeModal(cm);
      return;
    }
    /* colour swatch → drive its hidden select */
    var sw = e.target.closest('[data-epm-bundle-modal] [data-swatch-value]');
    if (sw && !sw.disabled) {
      var group = sw.closest('[data-swatch-group]');
      var sel = group && group.querySelector('select[data-bundle-variant]');
      if (!sel) return;
      sel.value = sw.getAttribute('data-swatch-value');
      group.querySelectorAll('[data-swatch-value]').forEach(function (b) {
        var on = b === sw;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var nm = group.querySelector('[data-swatch-name]');
      if (nm) nm.textContent = sw.getAttribute('title');
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    /* confirm add inside a modal → let the add handler run, then close */
    var addBtn = e.target.closest('[data-epm-bundle-modal] [data-epm-bundle-add]');
    if (addBtn) {
      var pm = addBtn.closest('[data-epm-bundle-modal]');
      setTimeout(function () { if (pm) closeModal(pm); }, 1100);
    }
  });

  /* variant select change → swap that slot's id on the confirm button */
  document.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-epm-bundle-modal] select[data-bundle-variant]');
    if (!sel) return;
    var modal = sel.closest('[data-epm-bundle-modal]');
    var btn = modal.querySelector('[data-epm-bundle-add]');
    if (!btn) return;
    var ids = (btn.getAttribute('data-epm-bundle-add') || '').split(',');
    var slot = parseInt(sel.getAttribute('data-slot'), 10);
    if (slot >= 0 && slot < ids.length) ids[slot] = sel.value;
    btn.setAttribute('data-epm-bundle-add', ids.join(','));
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('[data-epm-bundle-modal]:not([hidden])');
    if (open) closeModal(open);
  });

  /* teleport to <body> so position:fixed can't be trapped by ancestors */
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-epm-bundle-modal]').forEach(function (m) {
      document.body.appendChild(m);
    });
  });
})();


/* ── Fit bundle card titles: shrink until the one-line title fits its
   column, at any card width. ResizeObserver re-fits whenever the card's
   real size changes (fonts loading, layout settling, viewport resizes). ── */
(function () {
  'use strict';
  function fitNames() {
    document.querySelectorAll('.epm-bundle-card:not([hidden]) .epm-bundle-name').forEach(function (el) {
      if (!el.clientWidth) return;
      var card = el.closest('.epm-bundle-card');
      var thumbs = card.querySelector('.epm-bundle-thumbs');
      var key = thumbs ? thumbs.getAttribute('data-count') : '0';
      /* every card with the same layout (thumb count) is sized together, so
         switching state tabs never changes the title size — the longest
         title in the group decides for all of them */
      var titles = [];
      document.querySelectorAll('.epm-bundle-card').forEach(function (c) {
        var th = c.querySelector('.epm-bundle-thumbs');
        var k = th ? th.getAttribute('data-count') : '0';
        var n = c.querySelector('.epm-bundle-name');
        if (k === key && n) titles.push(n.textContent);
      });
      var original = el.textContent;
      var unified = null;
      titles.forEach(function (t) {
        el.textContent = t;
        el.style.fontSize = '';
        el.style.whiteSpace = '';
        var size = parseFloat(getComputedStyle(el).fontSize) || 13.5;
        var guard = 14;
        while (el.scrollWidth > el.clientWidth + 1 && size > 9.4 && guard--) {
          size -= 0.5;
          el.style.fontSize = size + 'px';
        }
        if (el.scrollWidth <= el.clientWidth + 1) {
          unified = unified === null ? size : Math.min(unified, size);
        }
      });
      el.textContent = original;
      /* one line is only worth it at a readable size — below 9.5px the whole
         group renders at the stylesheet size and wraps instead */
      if (unified !== null && unified < 9.5) unified = null;
      el.style.fontSize = unified === null ? '' : unified + 'px';
      el.style.whiteSpace = '';
      if (el.scrollWidth > el.clientWidth + 1) el.style.whiteSpace = 'normal';
    });
  }
  var t;
  function queue() { clearTimeout(t); t = setTimeout(fitNames, 80); }
  window.addEventListener('resize', queue);
  window.addEventListener('load', queue);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(queue);
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.epm-filter-btn')) queue();
  });
  function observeCards() {
    if (!window.ResizeObserver) return;
    var ro = new ResizeObserver(queue);
    document.querySelectorAll('.epm-bundle-card').forEach(function (c) { ro.observe(c); });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observeCards(); queue(); });
  } else {
    observeCards();
    queue();
  }
})();
